import { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Alert,
  Chip,
  CircularProgress,
  TextField,
  useTheme,
  alpha,
} from '@mui/material';
import { Phone, PhoneCall } from 'lucide-react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { usePhoneActions } from '../../hooks/phone/usePhoneActions';
import { useRegion } from '../../hooks/phone/useRegion';
import { useSubaccount } from '../../hooks/phone/useSubaccount';
import { useOrganisationActions } from '../../hooks/organisation/useOrganizationActions';
import { getSupportedRegionNames } from '../../config/regions';
import { PurchaseAddress } from '../../types/phone-numbers';
import TestCallDialog from '../../components/phone/TestCallDialog';
import ConfirmationDialog from '../../components/common/ConfirmationDialog';
import Notifications from '../../components/common/Notifications';
import { NotificationT, SEVERITY } from '../../types/notification';
import PhoneNumbersTab from './PhoneNumbersTab';

const PhoneView = () => {
  const theme = useTheme();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);
  const [notification, setNotification] = useState<NotificationT>({
    open: false,
    message: '',
    severity: SEVERITY.SUCCESS,
  });

  const [address, setAddress] = useState<PurchaseAddress>({
    customerName: '',
    street: '',
    city: '',
    region: '',
    postalCode: '',
    isoCountry: 'AU',
    abn: '',
  });
  const [addressError, setAddressError] = useState<string | null>(null);
  const [welcomeMessage, setWelcomeMessage] = useState('');
  const [isSavingWelcome, setIsSavingWelcome] = useState(false);

  const selectedOrganization = useSelector(
    (state: RootState) => state.organizations.selectedOrganisation
  );

  const { regionConfig, isSupported } = useRegion();
  const { initiateTestCall, isLoading, remainingCalls, canMakeCall, maxCalls } = usePhoneActions();
  const { updateOrganisation } = useOrganisationActions();
  const {
    hasSubaccount,
    subaccountSid,
    isLoading: isLoadingSubaccount,
    isCreating,
    isDeleting,
    fetchSubaccount,
    createSubaccount,
    deleteSubaccount,
  } = useSubaccount();

  useEffect(() => {
    fetchSubaccount();
  }, [fetchSubaccount]);

  useEffect(() => {
    if (selectedOrganization?.phoneWelcomeMessage !== undefined) {
      setWelcomeMessage(selectedOrganization.phoneWelcomeMessage);
    }
  }, [selectedOrganization?.phoneWelcomeMessage]);

  const handleSaveWelcomeMessage = async () => {
    if (!selectedOrganization?.id) return;
    setIsSavingWelcome(true);
    try {
      await updateOrganisation(selectedOrganization.id, { phoneWelcomeMessage: welcomeMessage });
      setNotification({
        open: true,
        message: 'Welcome message saved successfully.',
        severity: SEVERITY.SUCCESS,
      });
    } catch {
      setNotification({
        open: true,
        message: 'Failed to save welcome message.',
        severity: SEVERITY.ERROR,
      });
    } finally {
      setIsSavingWelcome(false);
    }
  };

  if (!selectedOrganization?.id) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">Please select an organization to access phone settings.</Alert>
      </Box>
    );
  }

  const isBookingEnabled = selectedOrganization?.isBookingEnabled;

  if (!isSupported || !isBookingEnabled) {
    const supportedRegions = getSupportedRegionNames().join(', ');
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          {!isBookingEnabled
            ? 'Phone features are only available for organizations with booking enabled.'
            : `Phone features are currently only available for supported regions (${supportedRegions}). Your organization timezone is set to: ${selectedOrganization?.company?.timezone || 'Not configured'}`}
        </Alert>
      </Box>
    );
  }

  const handleCall = async (phoneNumber: string) => {
    try {
      await initiateTestCall(phoneNumber);
      setNotification({
        open: true,
        message: 'Call initiated successfully!',
        severity: SEVERITY.SUCCESS,
      });
    } catch (err) {
      setNotification({
        open: true,
        message: err instanceof Error ? err.message : 'Failed to initiate call',
        severity: SEVERITY.ERROR,
      });
      throw err;
    }
  };

  const handleCreateSubaccount = async () => {
    // Validate address fields
    if (
      !address.customerName.trim() ||
      !address.street.trim() ||
      !address.city.trim() ||
      !address.region.trim() ||
      !address.postalCode.trim() ||
      !address.abn?.trim()
    ) {
      setAddressError('Please fill in all fields including ABN before activating.');
      return;
    }
    if (address.abn!.replace(/\s/g, '').length !== 11) {
      setAddressError('ABN must be exactly 11 digits.');
      return;
    }
    setAddressError(null);

    try {
      await createSubaccount(selectedOrganization?.companyName, address);
      setNotification({
        open: true,
        message: 'Phone system activated successfully!',
        severity: SEVERITY.SUCCESS,
      });
    } catch {
      setNotification({
        open: true,
        message: 'Failed to activate phone system. Please try again.',
        severity: SEVERITY.ERROR,
      });
    }
  };

  const handleDeactivate = async () => {
    setDeactivateDialogOpen(false);
    try {
      await deleteSubaccount();
      setNotification({
        open: true,
        message: 'Phone system deactivated successfully.',
        severity: SEVERITY.SUCCESS,
      });
    } catch {
      setNotification({
        open: true,
        message: 'Failed to deactivate phone system. Please try again.',
        severity: SEVERITY.ERROR,
      });
    }
  };

  if (isLoadingSubaccount) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          mb: 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 500 }}>
            Phone
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {hasSubaccount
              ? 'Manage your phone numbers and routing settings'
              : 'Test our AI voice agent and activate your phone system'}
          </Typography>
        </Box>

        {hasSubaccount && (
          <Button
            variant="outlined"
            color="error"
            size="small"
            onClick={() => setDeactivateDialogOpen(true)}
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={14} color="inherit" /> : undefined}
          >
            {isDeleting ? 'Deactivating...' : 'Deactivate'}
          </Button>
        )}
      </Box>

      {hasSubaccount && subaccountSid ? (
        <>
          {/* Welcome Message */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              mb: 3,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
            }}
          >
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Welcome Message
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: 'block' }}>
              This message will be spoken to callers when they first connect to your AI voice agent.
            </Typography>
            <TextField
              multiline
              minRows={3}
              maxRows={6}
              fullWidth
              size="small"
              placeholder="e.g. Welcome to our business! How can I help you today?"
              value={welcomeMessage}
              onChange={e => setWelcomeMessage(e.target.value)}
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5 }}>
              <Button
                variant="contained"
                disableElevation
                size="small"
                onClick={handleSaveWelcomeMessage}
                disabled={
                  isSavingWelcome ||
                  welcomeMessage === (selectedOrganization?.phoneWelcomeMessage ?? '')
                }
                startIcon={
                  isSavingWelcome ? <CircularProgress size={14} color="inherit" /> : undefined
                }
              >
                {isSavingWelcome ? 'Saving...' : 'Save'}
              </Button>
            </Box>
          </Paper>

          <PhoneNumbersTab subaccountSid={subaccountSid} />
        </>
      ) : (
        <>
          {/* Test Call Card */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              mb: 3,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <PhoneCall size={24} color={theme.palette.primary.main} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" gutterBottom>
                  Test Your Call
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Experience our AI voice agent firsthand. Enter your phone number and receive a
                  demo call to see how the AI handles conversations.
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 2,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip
                  label={`${remainingCalls} of ${maxCalls} calls remaining`}
                  size="small"
                  color={
                    remainingCalls === 0 ? 'error' : remainingCalls === 1 ? 'warning' : 'success'
                  }
                  variant="outlined"
                />
              </Box>

              <Button
                variant="contained"
                disableElevation
                startIcon={<Phone size={18} />}
                onClick={() => setDialogOpen(true)}
                disabled={!canMakeCall}
              >
                {canMakeCall ? 'Call Me Now' : 'Trial Limit Reached'}
              </Button>
            </Box>

            {!canMakeCall && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                You have used all {maxCalls} test calls for this organization. Contact support to
                request additional test calls.
              </Alert>
            )}
          </Paper>

          {/* Activate Voice CTA */}
          <Paper
            elevation={0}
            sx={{
              p: 4,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
            }}
          >
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  borderRadius: '50%',
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 2,
                }}
              >
                <Phone size={28} color={theme.palette.primary.main} />
              </Box>
              <Typography variant="h6" gutterBottom>
                Ready to Go Live?
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420, mx: 'auto' }}>
                Activate your phone system to purchase numbers, configure routing, and start
                receiving calls through your AI voice agent.
              </Typography>
            </Box>

            <Box sx={{ maxWidth: 480, mx: 'auto' }}>
              <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                Business Address
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                Required for purchasing phone numbers. This address will be registered with the
                phone provider.
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
                <TextField
                  label="Business Name"
                  value={address.customerName}
                  onChange={e => setAddress(prev => ({ ...prev, customerName: e.target.value }))}
                  fullWidth
                  size="small"
                  required
                />
                <TextField
                  label="ABN (Australian Business Number)"
                  value={address.abn}
                  onChange={e => {
                    const value = e.target.value.replace(/[^0-9\s]/g, '');
                    setAddress(prev => ({ ...prev, abn: value }));
                  }}
                  fullWidth
                  size="small"
                  required
                  placeholder="e.g. 51 824 753 556"
                  helperText={
                    address.abn?.trim() && address.abn.replace(/\s/g, '').length !== 11
                      ? 'ABN must be 11 digits'
                      : ''
                  }
                  error={!!address.abn?.trim() && address.abn.replace(/\s/g, '').length !== 11}
                  inputProps={{ maxLength: 14 }}
                />
                <TextField
                  label="Street Address"
                  value={address.street}
                  onChange={e => setAddress(prev => ({ ...prev, street: e.target.value }))}
                  fullWidth
                  size="small"
                  required
                />
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  <TextField
                    label="City"
                    value={address.city}
                    onChange={e => setAddress(prev => ({ ...prev, city: e.target.value }))}
                    size="small"
                    required
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    label="State / Region"
                    value={address.region}
                    onChange={e => setAddress(prev => ({ ...prev, region: e.target.value }))}
                    size="small"
                    required
                    sx={{ flex: 1 }}
                  />
                </Box>
                <TextField
                  label="Postal Code"
                  value={address.postalCode}
                  onChange={e => setAddress(prev => ({ ...prev, postalCode: e.target.value }))}
                  size="small"
                  required
                  sx={{ width: '50%' }}
                />
              </Box>

              {addressError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {addressError}
                </Alert>
              )}

              <Box sx={{ textAlign: 'center' }}>
                <Button
                  variant="contained"
                  disableElevation
                  onClick={handleCreateSubaccount}
                  disabled={isCreating}
                  startIcon={
                    isCreating ? <CircularProgress size={16} color="inherit" /> : undefined
                  }
                >
                  {isCreating ? 'Activating...' : 'Activate Voice'}
                </Button>
              </Box>
            </Box>
          </Paper>
        </>
      )}

      {regionConfig && (
        <TestCallDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          onCall={handleCall}
          isLoading={isLoading}
          remainingCalls={remainingCalls}
          maxCalls={maxCalls}
          regionConfig={regionConfig}
        />
      )}

      <ConfirmationDialog
        open={deactivateDialogOpen}
        title="Deactivate Phone System"
        message="This will permanently close your phone subaccount. All purchased phone numbers and routing configurations will be lost. This action cannot be undone."
        onConfirm={handleDeactivate}
        onCancel={() => setDeactivateDialogOpen(false)}
      />

      <Notifications
        open={notification.open}
        message={notification.message}
        severity={notification.severity}
        onClose={() => setNotification(prev => ({ ...prev, open: false }))}
      />
    </Box>
  );
};

export default PhoneView;
